import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getFieldNote } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE_URL, DEFAULT_OG_IMAGE, breadcrumbLd } from "@/lib/seo";
import { RelatedResources } from "@/components/RelatedResources";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";

// Inline links inside Field Notes: route internally for on-site paths, open
// external sources in a new tab.
const mdComponents = {
  a: ({ href, children }) => {
    if (href && href.startsWith("/")) {
      return (
        <Link to={href} data-testid="field-note-inline-link">
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function FieldNote() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const { openBrief } = useBrief();
  const [note, setNote] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    setStatus("loading");
    getFieldNote(slug)
      .then((d) => {
        if (!active) return;
        setNote(d);
        setStatus("ready");
        track("field_note_view", { slug });
      })
      .catch(() => active && setStatus("notfound"));
    return () => {
      active = false;
    };
  }, [slug]);

  const fade = {
    initial: { opacity: 0, y: reduce ? 0 : 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduce ? 0 : 0.5, ease: "easeOut" },
  };

  return (
    <div className="exec-scope min-h-screen">
      <article className="exec-container pt-24 md:pt-28 pb-24">
        <div className="max-w-3xl">
          <Link
            to="/resources"
            data-testid="field-note-back"
            className="exec-link"
          >
            <ArrowLeft className="h-4 w-4" /> All resources
          </Link>

          {status === "loading" && (
            <div className="mt-10 animate-pulse" data-testid="field-note-loading">
              <div className="h-3 w-28 bg-[color:var(--rule)]" />
              <div className="h-10 w-3/4 bg-[color:var(--rule)] mt-6" />
              <div className="h-10 w-1/2 bg-[color:var(--rule)] mt-3" />
              <div className="h-4 w-40 bg-[color:var(--rule)] mt-8" />
              <div className="space-y-3 mt-10">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-4 w-full bg-[color:var(--rule)]" />
                ))}
              </div>
            </div>
          )}

          {status === "notfound" && (
            <div className="mt-12" data-testid="field-note-notfound">
              <p className="exec-eyebrow">Field Note</p>
              <h1 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-4">
                This note isn’t available.
              </h1>
              <p className="exec-serif text-[18px] text-[color:var(--lead-ink)] mt-5 max-w-xl">
                It may have been moved or unpublished. The rest of the library is
                still here.
              </p>
              <Link
                to="/resources"
                className="exec-btn exec-btn-primary mt-7"
                data-testid="field-note-notfound-back"
              >
                Back to Resources <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "ready" && note && (
            <motion.div {...fade} data-testid="field-note-article">
              <Seo
                title={`${note.title} — Copper & Vine`}
                description={note.excerpt || note.title}
                path={`/resources/field-notes/${slug}`}
                type="article"
                image={note.image || DEFAULT_OG_IMAGE}
                jsonLd={[
                  {
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: note.title,
                    description: note.excerpt || undefined,
                    datePublished: note.published_at || undefined,
                    author: { "@type": note.author ? "Person" : "Organization", name: note.author || "Copper & Vine" },
                    publisher: { "@type": "Organization", name: "Copper & Vine", logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE } },
                    image: note.image || DEFAULT_OG_IMAGE,
                    mainEntityOfPage: `${SITE_URL}/resources/field-notes/${slug}`,
                  },
                  breadcrumbLd([
                    { name: "Home", path: "/" },
                    { name: "Resources", path: "/resources" },
                    { name: note.title, path: `/resources/field-notes/${slug}` },
                  ]),
                ]}
              />
              <header className="mt-9">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="exec-eyebrow">Field Note</p>
                  {(note.tags || []).map((t) => (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-[0.14em] font-normal px-2.5 py-0.5 rounded-[2px] border border-[color:var(--copper)]/45 text-[color:var(--copper-deep)]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <h1
                  className="font-extralight text-[clamp(30px,4.4vw,52px)] leading-[1.1] mt-5"
                  data-testid="field-note-title"
                >
                  {note.title}
                </h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-6">
                  <span className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--lead-ink)]">
                    {note.author || "Copper & Vine"}
                  </span>
                  {fmtDate(note.published_at) && (
                    <>
                      <span className="text-[color:var(--rule)]">/</span>
                      <span
                        className="text-[11px] tracking-[0.06em] text-[color:var(--grey)]"
                        data-testid="field-note-date"
                      >
                        {fmtDate(note.published_at)}
                      </span>
                    </>
                  )}
                </div>
              </header>

              <div className="h-px w-16 bg-[color:var(--copper)] my-9" />

              <div className="exec-article" data-testid="field-note-body">
                <ReactMarkdown components={mdComponents}>
                  {note.body || ""}
                </ReactMarkdown>
              </div>

              <RelatedResources
                currentType="note"
                currentSlug={note.slug || slug}
                topic={note.topic}
              />

              {/* Close */}
              <div className="mt-14 pt-10 border-t border-[color:var(--rule)]">
                <h2 className="font-light text-[clamp(24px,3vw,34px)] leading-[1.15]">
                  Facing the same decision?
                </h2>
                <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4 max-w-xl">
                  Score where you stand in ten questions, see the engagement built
                  for this, or request a senior read on the decision in front of you.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-4">
                  <Link
                    to="/scorecard"
                    data-testid="field-note-scorecard"
                    className="exec-btn exec-btn-primary"
                  >
                    Take the Scorecard <ArrowRight className="h-4 w-4" />
                  </Link>
                  {note.service?.path && (
                    <Link
                      to={note.service.path}
                      data-testid="field-note-service-link"
                      onClick={() => track("field_note_service_click", { slug: note.slug, service: note.service.path })}
                      className="exec-btn exec-btn-secondary"
                    >
                      {note.service.label || "See the service"}
                    </Link>
                  )}
                  <button
                    type="button"
                    data-testid="field-note-brief"
                    onClick={() => {
                      track("brief_open", { location: "field-note" });
                      openBrief("general");
                    }}
                    className="exec-link"
                  >
                    Request the brief
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </article>
    </div>
  );
}
